"use client";

import { FC, useEffect, useMemo, useActionState } from "react";
import { useLocale, useTranslations } from "next-intl";

import { IDatoGeneralMeta } from "@types";
import { getLink, getTranslatedHref } from "utils";
import { getSiteUrlForLocale } from "consts/locale";
import { subscribeToHubspotAction } from "app/actions";

import { SubmitButton } from "components/organisms/Form/fields/SubmitButton";

import s from "./Newsletter.module.scss";
import { Locale } from "consts/locale";

interface Props {
  general: IDatoGeneralMeta;
}

export const Newsletter: FC<Props> = ({ general }) => {
  const t = useTranslations("common");
  const locale = useLocale() as Locale;
  const [state, formAction, isPending] = useActionState(subscribeToHubspotAction, {});

  const newsletterTitle: string = useMemo(() => {
    if (general.newsletterOverrides?.officeName) {
      return `${t("subscribe-to-the-newsletter-of")} ${general.newsletterOverrides.officeName}`;
    }
    return t("subscribe-to-our-newsletter");
  }, [t, general.newsletterOverrides]);

  const officeName = general.newsletterOverrides?.officeName;
  // Office override wins; fall back to General only when the office has no segment.
  const hubspotNewsletterSegment =
    general.newsletterOverrides?.hubspotNewsletterSegment || general.hubspotGeneralSegment;
  const site = getSiteUrlForLocale(locale);

  const newsletterSuccessLink = useMemo(() => {
    if (general.newsletterSuccessPage) {
      const successLink = getLink(general.newsletterSuccessPage, locale);
      return `${site}${getTranslatedHref(successLink, locale)}`;
    }
    return null;
  }, [general.newsletterSuccessPage, site, locale]);

  const newsletterErrorLink = useMemo(() => {
    if (general.newsletterErrorPage) {
      const errorLink = getLink(general.newsletterErrorPage, locale);
      return `${site}${getTranslatedHref(errorLink, locale)}`;
    }
    return null;
  }, [general.newsletterErrorPage, site, locale]);

  useEffect(() => {
    if (state.success && newsletterSuccessLink) {
      window.location.href = newsletterSuccessLink;
    } else if (state.error && newsletterErrorLink) {
      window.location.href = newsletterErrorLink;
    }
  }, [state, newsletterSuccessLink, newsletterErrorLink]);

  if (!newsletterSuccessLink || !newsletterErrorLink) return null;
  return (
    <div className={s.Newsletter}>
      <p>
        <strong>{newsletterTitle}</strong>
      </p>
      <form action={formAction} className={s.Form}>
        <fieldset>
          {officeName && <input type="hidden" name="officeName" value={officeName} />}
          {hubspotNewsletterSegment && (
            <input type="hidden" name="hubspotNewsletterSegment" value={hubspotNewsletterSegment} />
          )}
          <div className={s.InputGroup}>
            <label htmlFor="name" className={s.Label}>
              {t("name")}
              <input
                id="name"
                type="text"
                name="name"
                placeholder={t("fill-in-your-name")}
                className={s.Input}
                required
              />
            </label>
            <label htmlFor="email" className={s.Label}>
              E-mail
              <input
                id="email"
                type="email"
                name="email"
                placeholder={t("fill-in-your-email-address")}
                className={s.Input}
                required
              />
            </label>
          </div>
          <div className={s.Submit}>
            <SubmitButton value={t("subscribe")} disabled={isPending} isLoading={isPending} />
          </div>
        </fieldset>
      </form>
    </div>
  );
};
